import { useEffect } from "react";
import { LOGO_URL } from "@/lib/api";

const SITE_URL = "https://elitepixels.ca";
const DEFAULT_OG = "https://images.pexels.com/photos/31953126/pexels-photo-31953126.jpeg";

function upsertMeta(attr, key, value) {
  if (value == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(value));
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"][data-seo="1"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, payload) {
  let el = document.head.querySelector(`script[data-seo-jsonld="${id}"]`);
  if (!payload) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

export default function SEO({
  title,
  description,
  path = "",
  image = DEFAULT_OG,
  type = "website",
  jsonLd = null,
}) {
  useEffect(() => {
    const fullTitle = title
      ? `${title} | Elite Pixels`
      : "Elite Pixels — Wedding Photography & Films, Told Honestly";
    const desc = description ||
      "Wedding photography and films created to preserve the moments and emotions you never want to forget — Toronto, across Canada, the United States, and wherever your story takes you.";
    const url = `${SITE_URL}${path}`;

    document.title = fullTitle;

    upsertMeta("name", "description", desc);
    upsertLink("canonical", url);
    upsertLink("icon", LOGO_URL);

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:site_name", "Elite Pixels");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", image);

    upsertJsonLd("page", jsonLd);

    return () => {
      // Leave tags in place (next page will overwrite) — avoids flash of missing meta
    };
  }, [title, description, path, image, type, jsonLd]);

  return null;
}

/* ---------- Schema generators (unchanged) ---------- */
export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#business`,
  name: "Elite Pixels",
  alternateName: "Elite Pixels Photography & Videography",
  url: SITE_URL,
  image: LOGO_URL,
  logo: LOGO_URL,
  description: "Wedding photography and films created to preserve the moments and emotions you never want to forget — across Canada, the United States, and wherever your story takes you.",
  telephone: "+1-647-640-3625",
  email: "muj.emal@elitepixels.ca",
  priceRange: "$$$",
  address: { "@type": "PostalAddress", addressLocality: "Toronto", addressRegion: "ON", addressCountry: "CA" },
  areaServed: ["Canada", "United States", "Worldwide"],
  sameAs: ["https://instagram.com/elitepixels_", "https://youtube.com/@elitepixels330"],
  aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "47" },
});

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Elite Pixels",
  url: SITE_URL,
  logo: LOGO_URL,
  sameAs: ["https://instagram.com/elitepixels_", "https://youtube.com/@elitepixels330"],
});

export const serviceSchema = (name, description) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  provider: { "@type": "LocalBusiness", name: "Elite Pixels", url: SITE_URL },
  description,
  areaServed: ["Canada", "United States", "Worldwide"],
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});

export const articleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  image: [post.cover_image],
  datePublished: post.created_at,
  dateModified: post.created_at,
  author: { "@type": "Organization", name: post.author || "Elite Pixels" },
  publisher: { "@type": "Organization", name: "Elite Pixels", logo: { "@type": "ImageObject", url: LOGO_URL } },
  description: post.excerpt,
});

export const videoSchema = (film) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: film.title,
  description: film.description || film.title,
  thumbnailUrl: `https://img.youtube.com/vi/${film.youtube_id}/maxresdefault.jpg`,
  embedUrl: `https://www.youtube.com/embed/${film.youtube_id}`,
  uploadDate: film.created_at,
});

export const reviewSchema = (testimonials) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Elite Pixels Wedding Photography",
  review: testimonials.map((t) => ({
    "@type": "Review",
    reviewRating: { "@type": "Rating", ratingValue: t.rating, bestRating: 5 },
    author: { "@type": "Person", name: t.name },
    reviewBody: t.review,
  })),
});
